import React, { useCallback, useEffect, useState } from "react";
import { Bug, Lightbulb, Sparkle, Wrench, CircleNotch, Check, EnvelopeSimple, Trash, ArrowClockwise } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { toast } from "sonner";

const TYPE_META = {
  bug: { icon: Bug, label: "Bug", tone: "text-neg border-neg/50 bg-neg-soft/20" },
  suggestion: { icon: Lightbulb, label: "Suggestion", tone: "text-pos border-pos/50 bg-pos-soft/20" },
  feature: { icon: Sparkle, label: "Feature", tone: "text-info border-info/50 bg-info-soft/20" },
  general: { icon: Wrench, label: "General", tone: "text-secondary-fg border-subtle bg-surface" },
};

const STATUS_FILTERS = [
  { k: "", label: "All" },
  { k: "new", label: "New" },
  { k: "read", label: "Read" },
  { k: "resolved", label: "Resolved" },
];

function RelTime({ iso }) {
  if (!iso) return null;
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  const out =
    diff < 60 ? `${diff}s ago`
    : diff < 3600 ? `${Math.floor(diff / 60)}m ago`
    : diff < 86400 ? `${Math.floor(diff / 3600)}h ago`
    : `${Math.floor(diff / 86400)}d ago`;
  return <span title={d.toLocaleString("en-IN")}>{out}</span>;
}

export default function AdminFeedback() {
  const [filter, setFilter] = useState("");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await api.get("/admin/feedback", { params });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load feedback");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.put(`/admin/feedback/${id}/status`, { status });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update status");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this feedback permanently?")) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/feedback/${id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="px-6 py-6 border-b border-subtle" data-testid="admin-feedback-section">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <EnvelopeSimple size={14} weight="fill" className="text-pos" />
        <span className="font-mono text-[12px] tracking-[0.22em] uppercase text-primary-fg">
          User Feedback
        </span>
        <span className="text-[10px] text-muted-fg font-mono">
          · {counts.new || 0} new · {counts.read || 0} read · {counts.resolved || 0} resolved
        </span>
        <div className="ml-auto flex items-center gap-1">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.k || "all"}
              type="button"
              onClick={() => setFilter(s.k)}
              data-testid={`feedback-filter-${s.k || "all"}`}
              className={
                "px-2 py-1 text-[10px] font-mono uppercase tracking-widest border rounded-sm transition " +
                (filter === s.k
                  ? "border-pos text-pos bg-pos-soft/30"
                  : "border-subtle text-muted-fg hover:text-primary-fg hover:border-pos/40")
              }
            >
              {s.label}
            </button>
          ))}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="feedback-reload"
            className="px-2 py-1 border border-subtle rounded-sm hover:bg-surface-hover text-[10px] font-mono uppercase tracking-widest text-secondary-fg flex items-center gap-1 disabled:opacity-50"
          >
            <ArrowClockwise size={11} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="flex items-center gap-2 py-10 text-muted-fg justify-center">
          <CircleNotch size={16} className="animate-spin" />
          <span className="text-xs font-mono uppercase tracking-widest">Loading feedback…</span>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-sm text-muted-fg" data-testid="feedback-empty">
          No feedback {filter ? `with status "${filter}"` : "yet"}.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((f) => {
            const meta = TYPE_META[f.type] || TYPE_META.general;
            const Icon = meta.icon;
            return (
              <div
                key={f.id}
                className={
                  "border rounded-sm p-3 bg-surface/50 " +
                  (f.status === "new" ? "border-pos/30" : "border-subtle")
                }
                data-testid={`feedback-row-${f.id}`}
              >
                <div className="flex items-start gap-2 flex-wrap">
                  <span
                    className={
                      "px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded-sm flex items-center gap-1 shrink-0 " +
                      meta.tone
                    }
                  >
                    <Icon size={10} weight="fill" />
                    {meta.label}
                  </span>
                  <span className="text-sm text-primary-fg font-medium flex-1 break-words">
                    {f.subject}
                  </span>
                  <span className="text-[10px] text-muted-fg font-mono shrink-0">
                    <RelTime iso={f.created_at} />
                  </span>
                </div>
                <div className="mt-1 text-[11px] text-muted-fg font-mono">
                  {f.user_name || "—"} &lt;{f.user_email}&gt;
                  {f.page && <span className="ml-2 text-secondary-fg/70">· {f.page}</span>}
                </div>
                <div className="mt-2 text-sm text-secondary-fg whitespace-pre-wrap leading-relaxed">
                  {f.message}
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span
                    className={
                      "px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded-sm " +
                      (f.status === "new"
                        ? "border-pos text-pos"
                        : f.status === "read"
                        ? "border-mod text-mod"
                        : "border-subtle text-muted-fg")
                    }
                  >
                    {f.status}
                  </span>
                  <div className="ml-auto flex items-center gap-1.5">
                    {f.status !== "read" && (
                      <button
                        type="button"
                        onClick={() => setStatus(f.id, "read")}
                        disabled={busyId === f.id}
                        data-testid={`feedback-mark-read-${f.id}`}
                        className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest border border-subtle rounded-sm text-secondary-fg hover:border-mod/60 hover:text-mod disabled:opacity-50"
                      >
                        Mark Read
                      </button>
                    )}
                    {f.status !== "resolved" && (
                      <button
                        type="button"
                        onClick={() => setStatus(f.id, "resolved")}
                        disabled={busyId === f.id}
                        data-testid={`feedback-resolve-${f.id}`}
                        className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest border border-pos/60 text-pos rounded-sm hover:bg-pos-soft/30 disabled:opacity-50 flex items-center gap-1"
                      >
                        <Check size={10} weight="bold" />
                        Resolve
                      </button>
                    )}
                    <a
                      href={`mailto:${f.user_email}?subject=Re: ${encodeURIComponent(f.subject)}`}
                      className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest border border-subtle rounded-sm text-secondary-fg hover:border-info/60 hover:text-info"
                      title="Reply via email"
                    >
                      Reply
                    </a>
                    <button
                      type="button"
                      onClick={() => remove(f.id)}
                      disabled={busyId === f.id}
                      data-testid={`feedback-delete-${f.id}`}
                      className="p-1 text-muted-fg hover:text-neg disabled:opacity-50"
                      title="Delete"
                    >
                      <Trash size={12} weight="regular" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
